import React, { useEffect, useMemo, useState } from "react";
import { Search, Trash2, Check, X, Star, ExternalLink, Loader2, Pencil, FileText, FileSpreadsheet } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatApiError, photoUrl } from "../lib/api";
import { toast } from "sonner";
import { MANAGERS as MANAGER_FALLBACK } from "../lib/managers";

const STATUS_OPTS = [
    { value: "all", label: "All", testid: "filter-all" },
    { value: "pending", label: "Pending", testid: "filter-pending" },
    { value: "approved", label: "Approved", testid: "filter-approved" },
    { value: "finalist", label: "Finalists", testid: "filter-finalist" },
    { value: "rejected", label: "Rejected", testid: "filter-rejected" },
];

const STATUS_BADGE = {
    pending: "bg-tmc-sand text-tmc-inkSoft",
    approved: "bg-tmc-moss/15 text-tmc-mossDark",
    finalist: "bg-tmc-terracotta/15 text-tmc-terracottaDark",
    rejected: "bg-red-100 text-red-700",
};

export default function AdminDashboard() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [managers, setManagers] = useState(MANAGER_FALLBACK);

    useEffect(() => {
        api.get("/managers").then(({ data }) => {
            if (Array.isArray(data) && data.length > 0) setManagers(data);
        }).catch(() => {});
    }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== "all") params.status_filter = filter;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/submissions", { params });
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load submissions"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const counts = useMemo(() => {
        const c = { all: items.length, pending: 0, approved: 0, finalist: 0, rejected: 0 };
        items.forEach((i) => {
            c[i.status] = (c[i.status] || 0) + 1;
        });
        return c;
    }, [items]);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/submissions/${id}/status`, { status });
            toast.success(`Marked as ${status}`);
            setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
            if (active?.id === id) setActive({ ...active, status });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this submission permanently? Votes for it will also be removed.")) return;
        try {
            await api.delete(`/submissions/${id}`);
            toast.success("Submission deleted");
            setItems((arr) => arr.filter((i) => i.id !== id));
            if (active?.id === id) setActive(null);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const onSearch = (e) => {
        e.preventDefault();
        fetchItems();
    };

    const exportPdf = async () => {
        try {
            const res = await api.get("/submissions/report.pdf", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.download = `tmc_submissions_report_${date}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to export PDF"));
        }
    };

    const exportXlsx = async () => {
        try {
            const res = await api.get("/submissions/report.xlsx", { responseType: "blob" });
            const url = URL.createObjectURL(
                new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                })
            );
            const a = document.createElement("a");
            a.href = url;
            const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.download = `tmc_submissions_report_${date}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to export Excel"));
        }
    };

    return (
        <div className="App min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1">
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-dashboard">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                        <div>
                            <div className="label-overline">Admin · Submissions</div>
                            <h1 className="font-serif text-4xl text-tmc-ink mt-3">All Moments</h1>
                            <p className="text-tmc-muted mt-2">
                                Review entries and curate finalists for the live vote.
                            </p>
                        </div>
                        <div className="flex gap-3 items-end w-full md:w-auto">
                            <form onSubmit={onSearch} className="relative flex-1 md:w-80" data-testid="search-form">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tmc-muted" />
                                <input
                                    className="tmc-input pl-10"
                                    placeholder="Search name, dept, story…"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    data-testid="search-input"
                                />
                            </form>
                            <button
                                onClick={exportPdf}
                                className="tmc-btn-primary text-sm py-2.5 px-5 shrink-0"
                                data-testid="export-pdf-btn"
                            >
                                <FileText className="w-4 h-4" /> Export PDF
                            </button>
                            <button
                                onClick={exportXlsx}
                                className="tmc-btn-secondary text-sm py-2.5 px-5 shrink-0"
                                data-testid="export-xlsx-btn"
                            >
                                <FileSpreadsheet className="w-4 h-4" /> Export Excel
                            </button>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-wrap gap-2 border-b border-tmc-line pb-1" data-testid="status-tabs">
                        {STATUS_OPTS.map((s) => (
                            <button
                                key={s.value}
                                onClick={() => setFilter(s.value)}
                                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                                    filter === s.value
                                        ? "bg-white border border-tmc-line border-b-white -mb-px text-tmc-terracotta"
                                        : "text-tmc-inkSoft hover:text-tmc-terracotta"
                                }`}
                                data-testid={s.testid}
                            >
                                {s.label}{" "}
                                <span className="text-xs text-tmc-muted ml-1">{counts[s.value] ?? 0}</span>
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="py-24 flex items-center justify-center text-tmc-muted">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                        </div>
                    ) : items.length === 0 ? (
                        <div className="py-24 text-center text-tmc-muted">No submissions yet.</div>
                    ) : (
                        <div className="mt-6 tmc-card overflow-hidden" data-testid="submissions-table">
                            <div className="grid grid-cols-12 px-6 py-3 border-b border-tmc-line bg-tmc-sand/40 label-overline text-tmc-muted">
                                <div className="col-span-1">Photo</div>
                                <div className="col-span-3">Person</div>
                                <div className="col-span-4">Story</div>
                                <div className="col-span-2">Status</div>
                                <div className="col-span-2 text-right">Actions</div>
                            </div>
                            {items.map((s) => (
                                <div
                                    key={s.id}
                                    className="grid grid-cols-12 px-6 py-4 border-b border-tmc-line items-center hover:bg-tmc-sand/30 transition-colors cursor-pointer"
                                    onClick={() => setActive(s)}
                                    data-testid={`row-${s.id}`}
                                >
                                    <div className="col-span-1">
                                        {s.photo_url && (
                                            <img
                                                src={photoUrl(s.photo_url)}
                                                alt=""
                                                className="w-12 h-12 object-cover rounded-lg border border-tmc-line"
                                            />
                                        )}
                                    </div>
                                    <div className="col-span-3 min-w-0">
                                        <div className="font-medium text-tmc-ink truncate">{s.name}</div>
                                        <div className="text-xs text-tmc-muted truncate">{s.department}</div>
                                        {s.manager && (
                                            <div className="text-xs text-tmc-muted truncate">Mgr: {s.manager}</div>
                                        )}
                                        <div className="text-xs text-tmc-muted truncate">{s.email}</div>
                                    </div>
                                    <div className="col-span-4 text-sm text-tmc-inkSoft line-clamp-2 pr-4">
                                        {s.description}
                                    </div>
                                    <div className="col-span-2">
                                        <span
                                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                                                STATUS_BADGE[s.status] || ""
                                            }`}
                                        >
                                            {s.status}
                                        </span>
                                    </div>
                                    <div className="col-span-2 flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                        <IconBtn
                                            title="Approve"
                                            onClick={() => updateStatus(s.id, "approved")}
                                            tone="moss"
                                            testid={`approve-${s.id}`}
                                        >
                                            <Check className="w-4 h-4" />
                                        </IconBtn>
                                        <IconBtn
                                            title="Mark finalist"
                                            onClick={() => updateStatus(s.id, "finalist")}
                                            tone="terracotta"
                                            testid={`finalist-${s.id}`}
                                        >
                                            <Star className="w-4 h-4" />
                                        </IconBtn>
                                        <IconBtn
                                            title="Reject"
                                            onClick={() => updateStatus(s.id, "rejected")}
                                            tone="ink"
                                            testid={`reject-${s.id}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </IconBtn>
                                        <IconBtn
                                            title="Delete"
                                            onClick={() => remove(s.id)}
                                            tone="danger"
                                            testid={`delete-${s.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </IconBtn>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </main>

            {active && (
                <DetailModal
                    submission={active}
                    onClose={() => setActive(null)}
                    onStatus={(st) => updateStatus(active.id, st)}
                    onDelete={() => remove(active.id)}
                    onSaved={(updated) => {
                        setItems((arr) => arr.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
                        setActive((prev) => (prev ? { ...prev, ...updated } : prev));
                    }}
                />
            )}

            <Footer />
        </div>
    );
}

function IconBtn({ children, onClick, title, tone = "ink", testid }) {
    const tones = {
        moss: "text-tmc-mossDark hover:bg-tmc-moss/15",
        terracotta: "text-tmc-terracottaDark hover:bg-tmc-terracotta/15",
        ink: "text-tmc-inkSoft hover:bg-tmc-sand",
        danger: "text-red-600 hover:bg-red-50",
    };
    return (
        <button
            onClick={onClick}
            title={title}
            className={`p-2 rounded-lg transition-colors ${tones[tone]}`}
            data-testid={testid}
        >
            {children}
        </button>
    );
}

function DetailModal({ submission, onClose, onStatus, onDelete, onSaved, managers }) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(() => ({
        name: submission.name || "",
        department: submission.department || "",
        manager: submission.manager || "",
        email: submission.email || "",
        description: submission.description || "",
        donor_link: submission.donor_link || "",
    }));

    const cancel = () => {
        setForm({
            name: submission.name || "",
            department: submission.department || "",
            manager: submission.manager || "",
            email: submission.email || "",
            description: submission.description || "",
            donor_link: submission.donor_link || "",
        });
        setEditing(false);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/submissions/${submission.id}`, form);
            toast.success("Submission updated");
            onSaved?.(data);
            setEditing(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const inputClass = "tmc-input text-sm py-2";
    const labelClass = "label-overline block mb-1";

    return (
        <div
            className="fixed inset-0 bg-tmc-ink/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="detail-modal"
        >
            <div
                className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="bg-tmc-sand">
                        {submission.photo_url && (
                            <img
                                src={photoUrl(submission.photo_url)}
                                alt=""
                                className="w-full h-64 md:h-full object-cover"
                            />
                        )}
                    </div>
                    <div className="p-6 md:p-8">
                        {editing ? (
                            <div className="space-y-3" data-testid="modal-edit-form">
                                <div className="flex items-center justify-between">
                                    <div className="font-serif text-lg text-tmc-ink">Edit submission</div>
                                    <button
                                        onClick={cancel}
                                        className="text-sm text-tmc-muted hover:text-tmc-ink"
                                        data-testid="modal-edit-cancel"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div>
                                    <label className={labelClass}>Name</label>
                                    <input
                                        className={inputClass}
                                        value={form.name}
                                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                        data-testid="edit-name"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Department</label>
                                    <input
                                        className={inputClass}
                                        value={form.department}
                                        onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))}
                                        data-testid="edit-department"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Manager</label>
                                    <input
                                        list="manager-options"
                                        className={inputClass}
                                        value={form.manager}
                                        onChange={(e) => setForm((p) => ({ ...p, manager: e.target.value }))}
                                        placeholder="Start typing a manager…"
                                        data-testid="edit-manager"
                                    />
                                    <datalist id="manager-options">
                                        {(managers || []).map((m) => (
                                            <option key={m} value={m} />
                                        ))}
                                    </datalist>
                                </div>
                                <div>
                                    <label className={labelClass}>Email</label>
                                    <input
                                        type="email"
                                        className={inputClass}
                                        value={form.email}
                                        onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                                        data-testid="edit-email"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>
                                        Description ({form.description.length}/600)
                                    </label>
                                    <textarea
                                        rows={5}
                                        maxLength={600}
                                        className={`${inputClass} resize-none`}
                                        value={form.description}
                                        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                        data-testid="edit-description"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Donor link (optional)</label>
                                    <input
                                        type="url"
                                        className={inputClass}
                                        value={form.donor_link}
                                        onChange={(e) => setForm((p) => ({ ...p, donor_link: e.target.value }))}
                                        data-testid="edit-donor"
                                    />
                                </div>
                                <button
                                    onClick={save}
                                    disabled={saving}
                                    className="tmc-btn-primary w-full mt-2"
                                    data-testid="modal-edit-save"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                    Save changes
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="label-overline">{submission.department}</div>
                                        <div className="font-serif text-2xl text-tmc-ink mt-2">{submission.name}</div>
                                        <div className="text-sm text-tmc-muted">{submission.email}</div>
                                        {submission.manager && (
                                            <div className="text-sm text-tmc-muted">Manager: {submission.manager}</div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setEditing(true)}
                                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-tmc-line text-tmc-inkSoft hover:bg-tmc-sand"
                                        data-testid="modal-edit-btn"
                                    >
                                        <Pencil className="w-3.5 h-3.5" /> Edit
                                    </button>
                                </div>
                                <p className="mt-5 text-tmc-inkSoft leading-relaxed text-base whitespace-pre-line">
                                    {submission.description}
                                </p>
                                {submission.donor_link && (
                                    <a
                                        href={submission.donor_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 mt-5 text-tmc-terracotta hover:underline text-sm"
                                        data-testid="modal-donor-link"
                                    >
                                        <ExternalLink className="w-4 h-4" /> Donor link
                                    </a>
                                )}
                                <div className="mt-8 flex flex-wrap gap-2">
                                    <button onClick={() => onStatus("approved")} className="tmc-btn-secondary text-sm py-2 px-4" data-testid="modal-approve">
                                        Approve
                                    </button>
                                    <button onClick={() => onStatus("finalist")} className="tmc-btn-primary text-sm py-2 px-4" data-testid="modal-finalist">
                                        Mark Finalist
                                    </button>
                                    <button onClick={() => onStatus("rejected")} className="tmc-btn-secondary text-sm py-2 px-4" data-testid="modal-reject">
                                        Reject
                                    </button>
                                    <button onClick={onDelete} className="text-sm py-2 px-4 text-red-600 hover:bg-red-50 rounded-full" data-testid="modal-delete">
                                        Delete
                                    </button>
                                </div>
                                <button onClick={onClose} className="mt-6 text-sm text-tmc-muted hover:text-tmc-ink" data-testid="modal-close">
                                    Close
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
